import { getIcon, getRenderTypeInfo } from "@/features/calendar/utils";
import { useModals } from "@/features/modals/hooks";
import { getTaskTitle } from "@/features/tasks/utils";
import { type Task } from "@repo/api";
import { Button, Icon, LightMode, Text, briefcaseIcon } from "@repo/skapa";
import { t, useTranslation } from "@repo/translation";
import {
  CoWorkerName,
  localizeTime,
  localizeTimeRange,
  openNewProjectModal,
  useSettings,
} from "@repo/ui";
import { trackAnalytics } from "@repo/utils";
import { addMinutes } from "date-fns";
import { useNavigate } from "react-router-dom";

import {
  ActionsCell,
  ActionsCellContent,
  DetailsCell,
  IconLabel,
  InfoCell,
  ItemIcon,
  LinkButton,
  TimeCell,
  TimeCellContent,
} from "./components";

export const TaskCells = ({
  task,
  backgroundColor,
}: {
  task: Task;
  backgroundColor?: string;
}) => {
  const navigate = useNavigate();
  const { pushModal } = useModals();
  const { dateFnsLocale } = useTranslation();
  const { task_configuration } = useSettings();

  const startDate = task.taskStartDateTime
    ? new Date(task.taskStartDateTime)
    : undefined;
  const endDate = task.taskEndDateTime
    ? new Date(task.taskEndDateTime)
    : startDate
      ? addMinutes(startDate, 30)
      : undefined;
  const dueDate =
    task.isTaskDueBy && task.taskDate ? new Date(task.taskDate) : undefined;

  const textColor = task.isDone
    ? LightMode.colourTextAndIcon3
    : LightMode.colourTextAndIcon1;

  const displayedTime = task.isTimeSet
    ? dueDate
      ? localizeTime(dueDate, dateFnsLocale)
      : startDate && endDate
        ? localizeTimeRange(startDate, endDate, dateFnsLocale)
        : "-"
    : t("label.all_day");

  return (
    <>
      <TimeCell backgroundColor={backgroundColor}>
        <TimeCellContent>
          <ItemIcon
            aria-hidden
            paths={getIcon(
              getRenderTypeInfo(task),
              task.isDone,
              task_configuration?.task_types
            )}
          />
          <Text textStyle="Body.S" color={textColor} lineThrough={task.isDone}>
            {displayedTime}
          </Text>
        </TimeCellContent>
      </TimeCell>
      <DetailsCell>
        <Text
          textStyle="Heading.XS"
          color={textColor}
          lineThrough={task.isDone}
          overflowHidden
        >
          {getTaskTitle(task)}
        </Text>
        {task.emailId && (
          <Text textStyle="Body.S" color={textColor}>
            {t("label.assigned_to")}:{" "}
            <CoWorkerName email={task.emailId} fragment />
          </Text>
        )}
      </DetailsCell>
      <InfoCell>
        {task.opportunityId ? (
          <IconLabel>
            <Icon aria-hidden paths={briefcaseIcon} />
            <LinkButton>
              <Button
                type="plain"
                size="small"
                text={task.projectName ?? task.opportunityId.toString()}
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  navigate(`/opportunity/${task.opportunityId}`);
                  trackAnalytics("open_project");
                }}
              />
            </LinkButton>
          </IconLabel>
        ) : (
          <LinkButton>
            <Button
              type="plain"
              size="small"
              text={t("action.add_to_project")}
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.preventDefault();
                e.stopPropagation();
                openNewProjectModal({
                  pushModal,
                  resetModal: true,
                  associatedTask: task,
                  onProjectCreated: (projectId: number) => {
                    navigate(`/opportunity/${projectId}/`);
                    trackAnalytics("new_project_created");
                  },
                  onSelectProject: (projectId: number) => {
                    navigate(`/opportunity/${projectId}/`);
                    trackAnalytics("manage_project");
                  },
                });
              }}
            />
          </LinkButton>
        )}
      </InfoCell>
      <InfoCell wide>
        {task.description && (
          <Text textStyle="Body.S" overflowHidden>
            {task.description}
          </Text>
        )}
      </InfoCell>
      <InfoCell wide />
      <ActionsCell>
        <ActionsCellContent>
          {task.isDone && (
            <Text textStyle="Body.S" color={textColor}>
              {t("label.completed")}
            </Text>
          )}
        </ActionsCellContent>
      </ActionsCell>
    </>
  );
};
